// ════════════════════════════════════════════════════════════════════════════
// Rendering the persisted attach block — the block that REPLACES what the
// model wrote (U11, §7.3), in the authored grammar ruled 2026-08-19.
//
// What is printed is a RENDER, not a dump: one fence, the do-not-modify
// caption, the two block directives, and one item per attached row, each a
// headline line with its own indented tagged field lines. The per-pillar
// projectors decide what an item SAYS; nothing here knows a pillar's fields.
//
// The START/END markers the first build printed are gone (ruled): the fence
// already carries the bound they marked.
//
// Everything printed here obeys `grammar.ts` — the bullet, the indent, the
// `key: value` shape and the four ceilings — so the parser that reads a stored
// block back (`parse-attach-document.ts`) reads exactly what this writes.
// ════════════════════════════════════════════════════════════════════════════

import {
  SENTINEL_CAPTION_PREFIX,
  SENTINEL_CAPTION_SUFFIX,
  SENTINEL_DATETIME_SEPARATOR,
  SENTINEL_DATETIME_TIME_SEPARATOR,
  SENTINEL_FENCE_MARKER,
  SENTINEL_RANGE_DATETIME_SEPARATOR,
  SENTINEL_RANGE_TIME_SEPARATOR,
} from './attached-result-constants.js';
import {
  ATTACH_DIRECTIVE_REGEX,
  ATTACH_FENCE_INFO_STRING,
  ATTACH_FIELD_INDENT,
  ATTACH_FIELD_SEPARATOR,
  ATTACH_ITEM_BULLET,
  ATTACH_MAX_BLOCK_LENGTH,
  ATTACH_MAX_FIELDS_PER_ITEM,
  ATTACH_MAX_ITEMS,
  ATTACH_MAX_LINE_LENGTH,
  AttachDirectiveKey,
  type AttachBlockDocument,
  type AttachFieldLine,
  type AttachItemBlock,
  type CapturedAtRange,
} from './grammar.js';
import { AttachedResultKind } from './enums.js';
import { attachPillarName } from './pillar-names.js';

const DATE_PAD_LENGTH = 2;
const PAD_CHARACTER = '0';
const NEWLINE = '\n';
const FOLD_TO = ' ';
/** Every way a captured value can carry a line break. A break inside a value
 *  would end the field and turn the rest into a sibling line, so all three
 *  spellings fold to one space — the smallest transform that keeps the value
 *  readable and the grammar intact. */
const LINE_BREAK_REGEX = /\r\n|\n|\r/g;
/** The space between the backtick run and the info string, as U11's ratified
 *  render printed it. Every reader trims the info string, so this changes
 *  nothing mechanically — it is here so a model meeting a stored block meets
 *  the exact line it has always met. */
const SENTINEL_FENCE_INFO_GAP = ' ';

function pad(input: { value: number }): string {
  return String(input.value).padStart(DATE_PAD_LENGTH, PAD_CHARACTER);
}

/**
 * The capture time as the caption prints it: `DD/MM/YYYY HH:mm`, in UTC.
 *
 * UTC is the choice, and it is about who reads this string. The caption is
 * persisted inside content that outlives the session, and is read later by
 * people in other timezones and by models with no timezone at all; a
 * local-zone rendering would make one capture read as two different times
 * depending on where it was rendered, with nothing in the block to say which.
 * The record keeps full ISO-8601 in `capturedAt`, so the caption is a READING
 * of the data rather than the data itself, and nothing is lost by choosing one
 * zone for it.
 *
 * An unparseable timestamp returns null rather than `NaN/NaN/NaN` — the caller
 * decides what to do with a record whose capture time it cannot state.
 */
export function captureDatetimeLabel(input: { capturedAt: string }): string | null {
  const captured = new Date(input.capturedAt);
  if (Number.isNaN(captured.getTime())) return null;
  const date = [
    pad({ value: captured.getUTCDate() }),
    pad({ value: captured.getUTCMonth() + 1 }),
    String(captured.getUTCFullYear()),
  ].join(SENTINEL_DATETIME_SEPARATOR);
  const time = [
    pad({ value: captured.getUTCHours() }),
    pad({ value: captured.getUTCMinutes() }),
  ].join(SENTINEL_DATETIME_TIME_SEPARATOR);
  return `${date} ${time}`;
}

/**
 * The capture WINDOW as the caption prints it (ruled 2026-08-25, decision 1).
 *
 * Beside `captureDatetimeLabel`, not instead of it: the single-instant helper
 * still serves every legacy envelope consumer, and this one composes it. Three
 * readings, ordered earliest→latest whatever order the ends arrive in:
 *
 *   same rendered minute  the single-instant form, byte-identical to what a
 *                         single-search block has always printed — the common
 *                         case changes nothing.
 *   same UTC day          `DD/MM/YYYY HH:mm–HH:mm`.
 *   cross-date            `DD/MM/YYYY HH:mm – DD/MM/YYYY HH:mm`.
 *
 * Honest as the block's WINDOW: it dates the merged capture span, and claims
 * nothing per item. Null when either end does not read as an instant — the
 * same refusal-to-print `captureDatetimeLabel` answers for one.
 */
export function captureRangeLabel(input: { range: CapturedAtRange }): string | null {
  const minTime = new Date(input.range.min).getTime();
  const maxTime = new Date(input.range.max).getTime();
  if (Number.isNaN(minTime) || Number.isNaN(maxTime)) return null;
  const [earlier, later] =
    minTime <= maxTime ? [input.range.min, input.range.max] : [input.range.max, input.range.min];
  const from = captureDatetimeLabel({ capturedAt: earlier });
  const to = captureDatetimeLabel({ capturedAt: later });
  if (from === null || to === null) return null;
  if (from === to) return from;
  const [fromDate] = from.split(' ');
  const [toDate, toTime] = to.split(' ');
  return fromDate === toDate && toTime !== undefined
    ? `${from}${SENTINEL_RANGE_TIME_SEPARATOR}${toTime}`
    : `${from}${SENTINEL_RANGE_DATETIME_SEPARATOR}${to}`;
}

/** The datetime the caption's center states — one instant's reading, or a
 *  range's. The two contracts of `AttachBlockDocument.capturedAt`, answered by
 *  the one formatter each owns. */
function captionLabelOf(input: { capturedAt: string | CapturedAtRange }): string | null {
  return typeof input.capturedAt === 'string'
    ? captureDatetimeLabel({ capturedAt: input.capturedAt })
    : captureRangeLabel({ range: input.capturedAt });
}

/** The caption line, whole. */
export function sentinelCaption(input: {
  capturedAt: string | CapturedAtRange;
}): string | null {
  const label = captionLabelOf({ capturedAt: input.capturedAt });
  return label === null
    ? null
    : `${SENTINEL_CAPTION_PREFIX}${label}${SENTINEL_CAPTION_SUFFIX}`;
}

/** One captured value on one line. Line breaks fold; the surrounding
 *  whitespace goes, because a leading space would be read as indentation. */
export function foldAttachValue(input: { value: string }): string {
  return input.value.replace(LINE_BREAK_REGEX, FOLD_TO).trim();
}

/** One item's headline line, or null when it does not survive the ceilings.
 *  The headline IS the item's identity, so an over-long one drops the WHOLE
 *  item rather than leaving its fields hanging under nothing. */
function headlineLineOf(input: { item: AttachItemBlock }): string | null {
  const line = `${ATTACH_ITEM_BULLET}${foldAttachValue({ value: input.item.headline })}`;
  return line.length > ATTACH_MAX_LINE_LENGTH ? null : line;
}

/** One field line, or null when it is over the ceiling. Dropped whole rather
 *  than trimmed, so a drop is visible instead of a value silently mangled into
 *  something that reads as complete. */
function fieldLineOf(input: { field: AttachFieldLine }): string | null {
  const line = [
    ATTACH_FIELD_INDENT,
    input.field.key,
    ATTACH_FIELD_SEPARATOR,
    foldAttachValue({ value: input.field.value }),
  ].join('');
  return line.length > ATTACH_MAX_LINE_LENGTH ? null : line;
}

/** One item's lines. Repeated keys stay in the order the projector wrote them
 *  — that order IS the sub-array (three `segment:` lines are a three-leg
 *  itinerary, in order). */
function itemLinesOf(input: { item: AttachItemBlock }): string[] {
  const headline = headlineLineOf({ item: input.item });
  if (headline === null) return [];
  const fields = input.item.fields
    .slice(0, ATTACH_MAX_FIELDS_PER_ITEM)
    .map((field) => fieldLineOf({ field }))
    .filter((line): line is string => line !== null);
  return [headline, ...fields];
}

/** The block's directives, in the order the grammar states them. `about` is
 *  omitted where the attach carries no framing of its own — an empty directive
 *  would say the block has one and that it is nothing.
 *
 *  The `kind` value is the PILLAR's name, taken from the one table that also
 *  reads it back (`pillar-names.ts`), never spelled here. A null pillar is the
 *  plain-link block (decision 8) and prints the plain-link family's own value
 *  — the one non-pillar name the directive can carry. */
function directiveLinesOf(input: { document: AttachBlockDocument }): string[] {
  const about = input.document.about === null
    ? null
    : foldAttachValue({ value: input.document.about });
  return [
    [
      AttachDirectiveKey.Kind,
      ATTACH_FIELD_SEPARATOR,
      input.document.pillar === null
        ? AttachedResultKind.Links
        : attachPillarName({ pillar: input.document.pillar }),
    ].join(''),
    ...(about === null || about.length === 0
      ? []
      : [`${AttachDirectiveKey.About}${ATTACH_FIELD_SEPARATOR}${about}`]),
  ].filter((line) => line.length <= ATTACH_MAX_LINE_LENGTH);
}

/** Items under the block ceiling: whole items are added while they fit, and
 *  the first one that would push the block past the ceiling stops the list.
 *  A partial item is never printed — the bound falls between items, where a
 *  reader can see that the list ended. `printedItemCount` is how many of the
 *  document's items made it in, so the caller can state the drop instead of
 *  inferring it from bytes. */
function itemLinesUnderCeiling(input: {
  document: AttachBlockDocument;
  headLength: number;
}): { lines: string[]; printedItemCount: number } {
  const { lines, printedItemCount } = input.document.items
    .slice(0, ATTACH_MAX_ITEMS)
    .reduce<{ lines: string[]; length: number; printedItemCount: number }>(
      (state, item) => {
        const lines = itemLinesOf({ item });
        if (lines.length === 0) return state;
        const length = state.length + lines.join(NEWLINE).length + NEWLINE.length;
        return length > ATTACH_MAX_BLOCK_LENGTH
          ? state
          : {
              lines: [...state.lines, ...lines],
              length,
              printedItemCount: state.printedItemCount + 1,
            };
      },
      { lines: [], length: input.headLength, printedItemCount: 0 },
    );
  return { lines, printedItemCount };
}

/** One rendered block and its loss accounting. `droppedItemCount` is the
 *  items of the document the ceilings kept out of the printed text — dropped
 *  BEFORE any parser could count them, which is why the render is where the
 *  number has to come from. A merged pillar block (2026-08-25 ruling) can
 *  carry more than the item ceiling in genuine rows, so the write seam holds
 *  this count for the notices channel to report. */
export interface SentinelBlockRender {
  readonly text: string;
  readonly droppedItemCount: number;
}

/**
 * The whole block with its drop accounting — fence lines included.
 *
 * THREE BACKTICKS ALWAYS, and a captured value cannot end the block early: a
 * fence closes on a line that is a backtick run and nothing else, and no line
 * this function emits can be one. Every body line is the fixed caption, a
 * directive (a letter, then its key), a blank line, an item (`- ` first) or a
 * field (indent, then its key) — so a value spelled ```` ``` ```` lands inside
 * a line that has other characters on it, which is text.
 *
 * Null when `capturedAt` does not read as an instant: a block whose caption
 * could not state when the data was true would be a do-not-modify claim over
 * an undated record, and the write seam refuses the attach instead of printing
 * one.
 */
export function renderSentinelBlockAccounted(input: {
  document: AttachBlockDocument;
}): SentinelBlockRender | null {
  const caption = sentinelCaption({ capturedAt: input.document.capturedAt });
  if (caption === null) return null;
  const head = [caption, ...directiveLinesOf({ document: input.document }), ''];
  const items = itemLinesUnderCeiling({
    document: input.document,
    headLength: head.join(NEWLINE).length,
  });
  return {
    text: [
      `${SENTINEL_FENCE_MARKER}${SENTINEL_FENCE_INFO_GAP}${ATTACH_FENCE_INFO_STRING}`,
      ...head,
      ...items.lines,
      SENTINEL_FENCE_MARKER,
    ].join(NEWLINE),
    droppedItemCount: input.document.items.length - items.printedItemCount,
  };
}

/** The rendered block alone — the read for every caller with no drop to
 *  report. Same bytes as the accounted render, by construction. */
export function renderSentinelBlock(input: {
  document: AttachBlockDocument;
}): string | null {
  return renderSentinelBlockAccounted(input)?.text ?? null;
}

/** Whether a rendered line would be read back as a block-level directive.
 *  Exported for the suites that prove the caption is not one: the caption is
 *  the only line of ours that is neither a directive nor an item, and a
 *  spelling that started matching would silently become a `kind`. */
export function readsAsDirective(input: { line: string }): boolean {
  return ATTACH_DIRECTIVE_REGEX.test(input.line);
}
